export const dailyAssignmentFilterKeys = [
  "cluster_cities",
  "offer_amount_bands",
  "bureau_bands",
  "pil_vintage_bands",
];

export const dailyAssignmentRowFields = [
  "call_banding",
  "no_attempt_0_7",
  "no_attempt_8_15",
  "no_attempt_16_30",
  "no_attempt_31_60",
  "no_attempt_61_90",
];

export function createEmptyDailyAssignmentFilters() {
  return Object.fromEntries(
    dailyAssignmentFilterKeys.map((key) => [key, []]),
  );
}

export function cloneDailyAssignmentFilters(filters) {
  return Object.fromEntries(
    dailyAssignmentFilterKeys.map((key) => [key, [...(filters[key] || [])]]),
  );
}

export function hasCompleteDailyAssignmentFilters(filters) {
  return dailyAssignmentFilterKeys.every(
    (key) => Array.isArray(filters[key]) && filters[key].length > 0,
  );
}

export function areDailyAssignmentFiltersEqual(first, second) {
  return dailyAssignmentFilterKeys.every((key) => {
    const firstValues = first?.[key] || [];
    const secondValues = second?.[key] || [];

    return (
      firstValues.length === secondValues.length &&
      firstValues.every((value, index) => value === secondValues[index])
    );
  });
}

export function getDailyAssignmentRowSignature(row) {
  return JSON.stringify(
    dailyAssignmentRowFields.map((field) => row?.[field] ?? null),
  );
}

export function calculateSelectedTarget(targets) {
  return Object.values(targets || {}).reduce((total, value) => {
    const numericValue = Number(value);
    return total + (Number.isInteger(numericValue) && numericValue > 0 ? numericValue : 0);
  }, 0);
}

export function validateDailyAssignmentTarget(rawValue, availableLeads) {
  const value = String(rawValue ?? "").trim();
  const maximum = Math.max(0, Number(availableLeads) || 0);

  if (value === "") {
    return { value: 0, error: "" };
  }

  if (!/^\d+$/.test(value)) {
    return { value: null, error: "Enter a non-negative whole number." };
  }

  const numericValue = Number(value);

  if (!Number.isSafeInteger(numericValue)) {
    return { value: null, error: "Enter a valid whole number." };
  }

  if (numericValue > maximum) {
    return {
      value: null,
      error: `Target cannot exceed ${maximum.toLocaleString("en-IN")} available leads.`,
    };
  }

  return { value: numericValue, error: "" };
}

export function buildDailyAssignmentSearchPayload(filters) {
  return { filters: cloneDailyAssignmentFilters(filters) };
}

export function getDailyAssignmentAgentId(agent) {
  return String(agent.id ?? (agent.profile_id != null
    ? `profile:${agent.profile_id}`
    : agent.username));
}

export function buildDailyAssignmentPayload({
  filters,
  selectedAgents,
  rows,
  targets,
}) {
  return {
    filters: cloneDailyAssignmentFilters(filters),
    target_agents: selectedAgents.map((agent) => ({
      username: agent.username,
      profile_id: agent.profile_id == null ? null : String(agent.profile_id),
    })),
    allocations: rows.flatMap((row) => {
      const targetNumber = Number(targets[getDailyAssignmentRowSignature(row)]) || 0;

      if (targetNumber <= 0) {
        return [];
      }

      return [
        {
          row_id: row.row_id,
          ...Object.fromEntries(
            dailyAssignmentRowFields.map((field) => [field, row[field]]),
          ),
          target_number: targetNumber,
        },
      ];
    }),
  };
}
